import axios from 'axios';
import { tokenManager } from '../services/tokenManager';
import { IS_LOCAL_ENV } from '../constants/environment'; // Import local environment flag

// Create an Axios instance with base configuration
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request Interceptor: Adds the Authorization header with the ID token (only if not in local env)
apiClient.interceptors.request.use(
  (config) => {
    try {
      if (!IS_LOCAL_ENV) {
        const idToken = tokenManager.getIdToken();
        if (idToken) {
          config.headers.Authorization = `Bearer ${idToken}`;
        }
      }
    } catch (error) {
      console.error('Error attaching ID token to request:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Attaches a small GraphQL summary to a rejection Error (not the full errors array).
 */
function attachGraphQLSummary(err, gqlError) {
  if (!gqlError) return err;
  const ext = gqlError.extensions || {};
  const statusFromExt = ext.httpStatus;
  err.graphqlSummary = {
    statusCode:
      statusFromExt != null ? statusFromExt : (err.statusCode ?? undefined),
    code: ext.code,
    message: gqlError.message,
    values: ext.values,
  };
  if (statusFromExt != null && err.statusCode == null) {
    err.statusCode = statusFromExt;
  }
  return err;
}

// Response Interceptor: Handles GraphQL errors
apiClient.interceptors.response.use(
  (response) => {
    if (response.data.errors) {
      // Opt-in escape hatch: some queries fetch several independently
      // permissioned slices in one round trip (e.g. Framework Agreement's
      // combined details query). The BFF resolves what it can and returns a
      // partial `data` object alongside per-field FORBIDDEN errors for the
      // slices the caller can't see. Rejecting the whole request there would
      // blank out sections the user *is* allowed to see, so callers that set
      // this flag (via `queryRequestTolerant`) get the raw response back and
      // do their own per-field handling instead of the generic rejects below.
      if (response.config?.allowPartialGraphQLErrors) {
        return response;
      }

      // console.log('response.data.errors', response.data.errors);
      // Check for CONFLICT error code and provide a professional message
      const conflictError = response.data.errors.find(
        (err) => err.extensions?.code === 'CONFLICT'
      );
      if (conflictError) {
        const conflictErrorObject = new Error('CONFLICT');
        conflictErrorObject.statusCode = 409;
        conflictErrorObject.sourceMessage = conflictError.message;
        attachGraphQLSummary(conflictErrorObject, conflictError);
        return Promise.reject(conflictErrorObject);
      }

      // Check for UNAUTHORIZED error code (403 Access Denied)
      const unauthorizedError = response.data.errors.find(
        (err) =>
          err.extensions?.code === 'UNAUTHORIZED' ||
          err.extensions?.httpStatus === 403
      );
      if (unauthorizedError) {
        const unauthorizedErrorObject = new Error('UNAUTHORIZED');
        unauthorizedErrorObject.statusCode = 403;
        unauthorizedErrorObject.sourceMessage = unauthorizedError.message;
        attachGraphQLSummary(unauthorizedErrorObject, unauthorizedError);
        return Promise.reject(unauthorizedErrorObject);
      }

      // Check for NOT_FOUND error code (404 Resource Not Found)
      const notFoundError = response.data.errors.find(
        (err) =>
          err.extensions?.code === 'NOT_FOUND' ||
          err.extensions?.httpStatus === 404
      );
      if (notFoundError) {
        const notFoundErrorObject = new Error('NOT_FOUND');
        notFoundErrorObject.statusCode = 404;
        notFoundErrorObject.sourceMessage = notFoundError.message;
        attachGraphQLSummary(notFoundErrorObject, notFoundError);
        return Promise.reject(notFoundErrorObject);
      }

      const firstError = response.data.errors[0];
      const graphqlError = new Error(
        response.data.errors.map((e) => e.message).join(', ')
      );
      attachGraphQLSummary(graphqlError, firstError);
      return Promise.reject(graphqlError);
    }
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Extracts the raw GraphQL query string from a GraphQL document node.
 * @param {object} query - GraphQL query document node.
 * @returns {string} The raw GraphQL query string.
 */
export const extractRawQuery = (query) => query.loc.source.body;

/**
 * Sends a GraphQL query request.
 * @param {object} query - GraphQL query document node.
 * @param {object} [variables={}] - Optional GraphQL query variables.
 * @returns {Promise} Axios response promise.
 */
export const queryRequest = (query, variables = {}) => {
  return apiClient.post('', { query: extractRawQuery(query), variables });
};

/**
 * Sends a GraphQL query request that tolerates partial, field-level errors.
 *
 * Use this instead of {@link queryRequest} when a query fetches multiple
 * independently-permissioned slices in one round trip and the caller wants to
 * render whatever data did resolve, surfacing per-field access-denied notices
 * for the slices that didn't — rather than having the whole request rejected
 * because *some* field came back FORBIDDEN. The raw axios response is
 * returned as-is (including `data.errors`); the caller is responsible for
 * pulling out the partial data and interpreting the errors (see
 * {@link extractForbiddenFields}).
 *
 * @param {object} query - GraphQL query document node.
 * @param {object} [variables={}] - Optional GraphQL query variables.
 * @returns {Promise} Axios response promise.
 */
export const queryRequestTolerant = (query, variables = {}) => {
  return apiClient.post(
    '',
    { query: extractRawQuery(query), variables },
    { allowPartialGraphQLErrors: true }
  );
};

/**
 * Sends a GraphQL mutation request.
 * @param {object} query - GraphQL mutation document node.
 * @param {object} [variables={}] - Optional GraphQL mutation variables.
 * @returns {Promise} Axios response promise.
 */
export const mutationRequest = (query, variables = {}) => {
  return apiClient.post('', { query: extractRawQuery(query), variables });
};

export default apiClient;
